import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { driverSupabase } from '@/integrations/supabase/driver-client';
import { ensureAdminAlertsChannel } from '@/lib/pushChannel';

type FcmData = Record<string, string>;

const LIFECYCLE_EVENTS = new Set([
  'driver_assigned',
  'driver_arrived',
  'trip_started',
  'trip_completed',
  'trip_cancelled',
]);

function eventTypeOf(data: Record<string, any>) {
  return String(data?.event_type || data?.type || '');
}

function isTripOffer(data: Record<string, any>) {
  const type = eventTypeOf(data);
  return type === 'trip_offer' || type === 'trip_offer_cancelled';
}

function isLifecycle(data: Record<string, any>) {
  return LIFECYCLE_EVENTS.has(eventTypeOf(data));
}

function routeFor(data: FcmData): string {
  const type = eventTypeOf(data);
  const requestId = data.request_id;
  const bookingRef = data.booking_number || data.booking_id;

  if (type === 'trip_offer' || type === 'trip_offer_cancelled') {
    if (requestId) return `/driver/home?offer=${encodeURIComponent(requestId)}`;
    return '/driver/home';
  }

  if (LIFECYCLE_EVENTS.has(type)) {
    if (type === 'trip_cancelled') return '/driver/home';
    return bookingRef ? `/driver/trips?focus=${encodeURIComponent(bookingRef)}` : '/driver/trips';
  }

  return '/driver/notifications';
}

function notificationTitle(data: Record<string, any>) {
  const type = eventTypeOf(data);
  return (
    data.title ||
    data.notification?.title ||
    ({
      driver_assigned: 'Trip Assigned',
      driver_arrived: 'Arrived at Pickup',
      trip_started: 'Trip Started',
      trip_completed: 'Trip Completed',
      trip_cancelled: 'Trip Cancelled',
    } as Record<string, string>)[type] ||
    'Driver Alert'
  );
}

function notificationBody(data: Record<string, any>) {
  return String(data.body || data.message || data.notification?.body || data.booking_number || 'Tap to view details');
}

async function showForegroundLifecycleNotification(data: FcmData) {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return;
  if (!isLifecycle(data)) return;

  try {
    await ensureAdminAlertsChannel();
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.schedule({
      notifications: [
        {
          id: Math.floor(Date.now() % 2147483647),
          title: notificationTitle(data),
          body: notificationBody(data),
          channelId: 'admin-alerts',
          smallIcon: 'ic_driver_notification',
          extra: data,
          schedule: { at: new Date(Date.now() + 100) },
        },
      ],
    });
  } catch (err) {
    console.warn('[driver-notifications] foreground local notification failed', err);
  }
}

export function useDriverNotifications(driverId?: string) {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() !== 'android') return;

    let cancelled = false;
    const cleanup: Array<{ remove: () => Promise<void> }> = [];

    (async () => {
      try {
        await ensureAdminAlertsChannel();

        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { LocalNotifications } = await import('@capacitor/local-notifications');

        const onReceived = await PushNotifications.addListener('pushNotificationReceived', async (notification) => {
          if (cancelled) return;
          const data = (notification.data || {}) as FcmData;
          if (isTripOffer(data)) return;

          window.dispatchEvent(new CustomEvent('driver:notification', {
            detail: {
              source: 'fcm',
              type: eventTypeOf(data),
              booking_number: data.booking_number || data.booking_id,
              data,
            },
          }));
          await showForegroundLifecycleNotification({
            ...data,
            title: notification.title || data.title,
            body: notification.body || data.body,
          });
        });

        const onTap = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const data = (action.notification?.data || {}) as FcmData;
          if (isTripOffer(data)) return;
          navRef.current(routeFor(data));
        });

        const onLocalTap = await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
          const data = (action.notification?.extra || {}) as FcmData;
          if (isTripOffer(data)) return;
          navRef.current(routeFor(data));
        });

        cleanup.push(onReceived, onTap, onLocalTap);
      } catch (err) {
        console.warn('[driver-notifications] init failed', err);
      }
    })();

    return () => {
      cancelled = true;
      cleanup.forEach((l) => { try { l.remove(); } catch { /* noop */ } });
    };
  }, []);

  useEffect(() => {
    if (!driverId) return;

    const channel = driverSupabase
      .channel(`driver-notifications-${driverId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${driverId}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (row?.recipient_type !== 'driver') return;
          if (row?.channel !== 'in_app') return;
          if (isTripOffer(row)) return;

          window.dispatchEvent(new CustomEvent('driver:notification', {
            detail: {
              source: 'realtime',
              type: row?.type,
              booking_number: row?.booking_number,
              row,
            },
          }));
        },
      )
      .subscribe();

    const poll = setInterval(async () => {
      try {
        const { data } = await driverSupabase
          .from('notifications' as any)
          .select('id')
          .eq('recipient_id', driverId)
          .eq('recipient_type', 'driver')
          .eq('channel', 'in_app')
          .eq('is_read', false)
          .limit(1);
        if (Array.isArray(data) && data.length > 0) {
          window.dispatchEvent(new CustomEvent('driver:notification', { detail: { source: 'poll' } }));
        }
      } catch { /* noop */ }
    }, 45000);

    return () => {
      driverSupabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [driverId]);
}
